'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useEffect, useRef, useState, ImgHTMLAttributes } from 'react';
import clsx from 'clsx';
import cloudflareLoader from '@/lib/imageLoader';
import { getGlobalOptimizePatterns } from './ImageLoader';
interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'loading'> {
  src?: string | null;
  alt: string;
  fallback?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
  aspectRatio?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  priority?: boolean;
  /** URL patterns that should use Cloudflare optimization. Falls back to global patterns. */
  optimizePatterns?: string[];
}
export function LazyImage(t0) {
  const $ = _c(63);
  let alt;
  let aspectRatio;
  let className;
  let fallback;
  let onError;
  let onLoad;
  let optimizePatterns;
  let props;
  let src;
  let style;
  let t1;
  let t2;
  let width;
  if ($[0] !== t0) {
    const {
      src: t3,
      alt: t4,
      fallback: t5,
      onLoad: t6,
      onError: t7,
      aspectRatio: t8,
      objectFit: t9,
      priority: t10,
      optimizePatterns: t11,
      className: t12,
      style: t13,
      width: t14,
      height,
      ...t15
    } = t0;
    src = t3;
    alt = t4;
    fallback = t5;
    onLoad = t6;
    onError = t7;
    aspectRatio = t8;
    t1 = t9;
    t2 = t10;
    optimizePatterns = t11;
    className = t12;
    style = t13;
    width = t14;
    props = t15;
    $[0] = t0;
    $[1] = alt;
    $[2] = aspectRatio;
    $[3] = className;
    $[4] = fallback;
    $[5] = onError;
    $[6] = onLoad;
    $[7] = optimizePatterns;
    $[8] = props;
    $[9] = src;
    $[10] = style;
    $[11] = t1;
    $[12] = t2;
    $[13] = width;
  } else {
    alt = $[1];
    aspectRatio = $[2];
    className = $[3];
    fallback = $[4];
    onError = $[5];
    onLoad = $[6];
    optimizePatterns = $[7];
    props = $[8];
    src = $[9];
    style = $[10];
    t1 = $[11];
    t2 = $[12];
    width = $[13];
  }
  const objectFit = t1 === undefined ? "cover" : t1;
  const priority = t2 === undefined ? false : t2;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  let t3;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const img = imgRef.current;
      if (!img) {
        return;
      }
      if (img.complete && img.naturalWidth > 0) {
        setIsLoaded(true);
      } else {
        if (img.complete && img.naturalWidth === 0) {
          setHasError(true);
        }
      }
    };
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== src) {
    t4 = [src];
    $[15] = src;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  useEffect(t3, t4);
  let t5;
  if ($[17] !== aspectRatio || $[18] !== style) {
    t5 = {
      aspectRatio,
      ...style
    };
    $[17] = aspectRatio;
    $[18] = style;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const containerStyle = t5;
  let t6;
  if ($[20] !== src) {
    t6 = src && src.trim() !== "";
    $[20] = src;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const isValidSrc = t6;
  let t7;
  if ($[22] !== onLoad) {
    t7 = () => {
      setIsLoaded(true);
      setHasError(false);
      onLoad?.();
    };
    $[22] = onLoad;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const handleImageLoad = t7;
  let t8;
  if ($[24] !== onError) {
    t8 = () => {
      setHasError(true);
      setIsLoaded(false);
      onError?.();
    };
    $[24] = onError;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const handleImageError = t8;
  let t9;
  if ($[26] !== optimizePatterns) {
    t9 = optimizePatterns ?? getGlobalOptimizePatterns();
    $[26] = optimizePatterns;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const patternsToUse = t9;
  let t10;
  if ($[28] !== isValidSrc || $[29] !== patternsToUse || $[30] !== src) {
    t10 = isValidSrc && (!patternsToUse || patternsToUse.some(pattern => src.includes(pattern)));
    $[28] = isValidSrc;
    $[29] = patternsToUse;
    $[30] = src;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const shouldOptimize = t10;
  if (!isValidSrc || hasError && fallback) {
    let t11;
    if ($[32] !== fallback) {
      t11 = fallback || <div className="flex items-center justify-center bg-white h-full"><div className="text-center p-4"><svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><p className="text-xs text-gray-500">Image unavailable</p></div></div>;
      $[32] = fallback;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== className || $[35] !== containerStyle || $[36] !== t11) {
      t12 = <div className={className} style={containerStyle}>{t11}</div>;
      $[34] = className;
      $[35] = containerStyle;
      $[36] = t11;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    return t12;
  }
  let t11;
  if ($[38] !== className) {
    t11 = clsx("relative overflow-hidden bg-gray-200", className);
    $[38] = className;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== hasError || $[41] !== isLoaded) {
    t12 = !isLoaded && !hasError && <div className="absolute inset-0 bg-white animate-pulse" />;
    $[40] = hasError;
    $[41] = isLoaded;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== alt || $[44] !== handleImageError || $[45] !== handleImageLoad || $[46] !== isLoaded || $[47] !== objectFit || $[48] !== priority || $[49] !== props || $[50] !== shouldOptimize || $[51] !== src || $[52] !== width) {
    t13 = shouldOptimize ? <Image ref={imgRef} src={src} alt={alt} fill={true} sizes={width ? `${width}px` : "100vw"} loader={cloudflareLoader} loading={priority ? "eager" : "lazy"} priority={priority} className={clsx("absolute inset-0 w-full h-full transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")} style={{
      objectFit
    }} onLoad={handleImageLoad} onError={handleImageError} /> : <img ref={imgRef} src={src} alt={alt} loading={priority ? "eager" : "lazy"} className={clsx("absolute inset-0 w-full h-full transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")} style={{
      objectFit
    }} onLoad={handleImageLoad} onError={handleImageError} {...props} />;
    $[43] = alt;
    $[44] = handleImageError;
    $[45] = handleImageLoad;
    $[46] = isLoaded;
    $[47] = objectFit;
    $[48] = priority;
    $[49] = props;
    $[50] = shouldOptimize;
    $[51] = src;
    $[52] = width;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== fallback || $[55] !== hasError) {
    t14 = hasError && !fallback && <div className="absolute inset-0 flex items-center justify-center bg-white"><div className="text-center p-4"><svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><p className="text-xs text-gray-500">Image unavailable</p></div></div>;
    $[54] = fallback;
    $[55] = hasError;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== containerStyle || $[58] !== t11 || $[59] !== t12 || $[60] !== t13 || $[61] !== t14) {
    t15 = <div className={t11} style={containerStyle}>{t12}{t13}{t14}</div>;
    $[57] = containerStyle;
    $[58] = t11;
    $[59] = t12;
    $[60] = t13;
    $[61] = t14;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  return t15;
}